/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Text, TouchableOpacity, View } from 'react-native';
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../domain/catalogTypes';
import { formatQty } from '@shared/quantity';
import { formatMoney } from '../../shared/format/public-api';
import { bulkCoefficientModalStyles as styles } from './bulkCoefficientModal.styles';
import { BULK_COEFFICIENT_PREVIEW_PAGE_SIZE } from '../application/bulkCoefficientConstants';
import type { BulkApplyTo } from './useBulkCoefficientModal';

type BulkCoefficientPreviewProps = {
  narrow: boolean;
  bulkApplyTo: BulkApplyTo;
  previewRows: InventoryBulkPreviewRow[];
  /** Filas listadas en la preview (≤ 500). */
  previewListedCount: number;
  /** Total que se aplicará al confirmar. */
  applyTotal: number;
  unpricedScopeCount?: number;
};

const formatPreviewValue = (item: InventoryBulkPreviewRow, value: number, bulkApplyTo: BulkApplyTo) =>
  bulkApplyTo === 'prices' ? formatMoney(value) : formatQty(value, item.unitOfMeasure);

export const BulkCoefficientPreview = ({
  narrow,
  bulkApplyTo,
  previewRows,
  previewListedCount,
  applyTotal,
  unpricedScopeCount = 0,
}: BulkCoefficientPreviewProps) => {
  const previewTruncated = applyTotal > previewListedCount;
  const [page, setPage] = useState(0);
  const listRef = useRef<FlatList<InventoryBulkPreviewRow>>(null);

  useEffect(() => {
    setPage(0);
  }, [previewRows]);

  const pageSize = BULK_COEFFICIENT_PREVIEW_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(previewRows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageStart = safePage * pageSize;

  useEffect(() => {
    if (page > pageCount - 1) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);
  const pageRows = useMemo(
    () => previewRows.slice(pageStart, pageStart + pageSize),
    [previewRows, pageStart, pageSize],
  );
  const rangeFrom = previewRows.length === 0 ? 0 : pageStart + 1;
  const rangeTo = Math.min(pageStart + pageSize, previewRows.length);
  const showPager = previewRows.length > pageSize;

  const goToPage = useCallback((next: number) => {
    setPage(next);
    listRef.current?.scrollToOffset({ offset: 0, animated: true });
  }, []);

  const renderItem = useCallback(
    ({ item }: { item: InventoryBulkPreviewRow }) =>
      narrow ? (
        <View style={styles.previewRow}>
          <Text style={styles.previewName} numberOfLines={2}>
            {item.name}
          </Text>
          <View style={styles.previewMobileGrid}>
            <View style={styles.previewMobileCol}>
              <Text style={styles.previewMobileLabel}>Actual</Text>
              <Text style={styles.previewOld} numberOfLines={1}>
                {formatPreviewValue(item, item.currentValue, bulkApplyTo)}
              </Text>
            </View>
            <View style={styles.previewMobileCol}>
              <Text style={styles.previewMobileLabel}>Nuevo</Text>
              <Text style={styles.previewNew} numberOfLines={1}>
                {formatPreviewValue(item, item.newValue, bulkApplyTo)}
              </Text>
            </View>
          </View>
        </View>
      ) : (
        <View style={styles.previewRowWide}>
          <Text style={styles.previewNameWide} numberOfLines={2}>
            {item.name}
          </Text>
          <Text style={styles.previewOldWide} numberOfLines={1}>
            {formatPreviewValue(item, item.currentValue, bulkApplyTo)}
          </Text>
          <Text style={styles.previewNewWide} numberOfLines={1}>
            {formatPreviewValue(item, item.newValue, bulkApplyTo)}
          </Text>
        </View>
      ),
    [bulkApplyTo, narrow],
  );

  const listHeader = (
    <View style={styles.previewSummary}>
      {bulkApplyTo === 'prices' && unpricedScopeCount > 0 ? (
        <Text style={styles.previewSummaryWarn}>
          {unpricedScopeCount} sin precio en el alcance (no entran).
        </Text>
      ) : null}
      {previewTruncated ? (
        <Text style={styles.previewSummaryWarn}>
          Mostramos {previewListedCount} para revisar. Al confirmar se aplican {applyTotal}.
        </Text>
      ) : null}
      {!narrow ? (
        <View style={styles.previewTableHead}>
          <Text style={styles.previewThProduct}>Producto</Text>
          <Text style={styles.previewThNum}>Actual</Text>
          <Text style={styles.previewThNum}>Nuevo</Text>
        </View>
      ) : null}
    </View>
  );

  const listFooter = showPager ? (
    <View style={styles.previewPager}>
      <Text style={styles.previewPagerMeta}>
        {rangeFrom}–{rangeTo} de {previewRows.length}
      </Text>
      <View style={styles.previewPagerActions}>
        <TouchableOpacity
          style={[styles.previewPagerBtn, safePage === 0 && styles.previewPagerBtnDisabled]}
          onPress={() => goToPage(Math.max(0, safePage - 1))}
          disabled={safePage === 0}
          accessibilityRole="button"
          accessibilityLabel="Página anterior"
        >
          <Text style={[styles.previewPagerBtnText, safePage === 0 && styles.previewPagerBtnTextDisabled]}>
            Anterior
          </Text>
        </TouchableOpacity>
        <Text style={styles.previewPagerIndex}>
          {safePage + 1} / {pageCount}
        </Text>
        <TouchableOpacity
          style={[
            styles.previewPagerBtn,
            safePage >= pageCount - 1 && styles.previewPagerBtnDisabled,
          ]}
          onPress={() => goToPage(Math.min(pageCount - 1, safePage + 1))}
          disabled={safePage >= pageCount - 1}
          accessibilityRole="button"
          accessibilityLabel="Página siguiente"
        >
          <Text
            style={[
              styles.previewPagerBtnText,
              safePage >= pageCount - 1 && styles.previewPagerBtnTextDisabled,
            ]}
          >
            Siguiente
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  ) : previewRows.length > 0 ? (
    <Text style={styles.previewPagerMetaOnly}>
      {previewRows.length} producto{previewRows.length === 1 ? '' : 's'}
    </Text>
  ) : null;

  return (
    <FlatList
      ref={listRef}
      data={pageRows}
      keyExtractor={(item) => item.id}
      style={styles.previewList}
      contentContainerStyle={styles.previewListContent}
      renderItem={renderItem}
      ListHeaderComponent={listHeader}
      ListFooterComponent={listFooter}
      initialNumToRender={20}
      maxToRenderPerBatch={24}
      windowSize={5}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator
      ListEmptyComponent={
        <Text style={styles.previewEmpty}>Sin productos en este alcance.</Text>
      }
    />
  );
};
